import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import {
  type QuizQuestionSet,
  type QuizFieldType,
  groupFieldsByQuestionSet,
  filterFieldsByQuestionLogic,
} from "@core/features/quiz";
import { Fieldset } from "@themes/pollinator/ui/Fieldset";
import { Form } from "@themes/pollinator/ui/Form";
import { StickyActionBar } from "@themes/pollinator/ui/StickyActionBar";
import { QuizFormField, type Props as QuizFormFieldProps } from "../QuizFormField";

export type Props = {
  /** Quiz question sets (grouped by header) */
  questionSets: QuizQuestionSet[];
  /** Form instance from useForm */
  form: UseFormReturn<Record<string, unknown>>;
  /** Whether the form has been modified */
  isDirty: boolean;
  /** Whether the form is valid */
  isValid: boolean;
  /** Whether the form is submitting */
  isSubmitting: boolean;
  /** Handler for form submission */
  onSubmit: () => void;
  /** Handler for cancel/reset */
  onCancel: () => void;
  /** Whether to show the cancel button (defaults to true when form is dirty) */
  showCancelButton?: boolean;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Container className */
  className?: string;
  /** Layout direction - vertical (default) or horizontal (profile-form style) */
  layout?: "vertical" | "horizontal";
};

/**
 * Styled quiz form component that renders questions grouped by fieldset.
 * Follows the ProfileForm pattern with support for horizontal layout.
 *
 * Questions are grouped by their header field into Fieldsets with subtle
 * caption-style headers. If there's only one question set, or if a group
 * has no header text, the fieldset header is hidden.
 *
 * This is a presentational component - business logic should be in the container.
 *
 * @example
 * ```tsx
 * <ProgramQuizForm
 *   questionSets={quiz.questionSets}
 *   form={form}
 *   isDirty={isDirty}
 *   isValid={isValid}
 *   isSubmitting={isSubmitting}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   layout="horizontal"
 * />
 * ```
 */
export function ProgramQuizForm({
  questionSets,
  form,
  isDirty,
  isValid,
  isSubmitting,
  onSubmit,
  onCancel,
  showCancelButton,
  primaryButtonText = "Save",
  className,
  layout = "horizontal",
}: Props) {
  // Determine whether to show cancel button
  const shouldShowCancel = showCancelButton ?? isDirty;

  // Group fields by question set
  const fieldGroups = useMemo(
    () => groupFieldsByQuestionSet(questionSets),
    [questionSets]
  );

  // Watch form values for conditional logic filtering
  const values = form.watch();

  // Filter all fields by conditional logic
  const visibleFieldGroups = useMemo(() => {
    return fieldGroups.map((group) => ({
      ...group,
      fields: filterFieldsByQuestionLogic(group.fields, values),
    }));
  }, [fieldGroups, values]);

  // Hide fieldset headers if there's only one group
  const hideFieldsetHeaders = visibleFieldGroups.length === 1;

  const renderField = (field: QuizFieldType) => {
    // QuizFormField omits labelVariant from QuizFieldType and redefines it
    // as TypeVariant, so we need to extract it and pass props explicitly
    const { labelVariant: _, ...fieldProps } = field;

    return (
      <QuizFormField
        key={field.name}
        {...(fieldProps as Omit<QuizFormFieldProps, "layout" | "labelVariant" | "yesNoVariant" | "radioGroupDirection">)}
        layout={layout}
        // Use caption variant to match FormField/ProfileForm label sizing
        labelVariant="caption"
        // Use chips variant for YesNoButtons to match SelectChips styling
        yesNoVariant="chips"
        // Stack radio options vertically for form layout
        radioGroupDirection="vertical"
      />
    );
  };

  return (
    <>
      <div className={twMerge("flex flex-col gap-4", className)}>
        <Form
          form={form}
          className="flex flex-col gap-6"
          errorContainerClassName="p-0 w-full max-w-full mt-8"
          innerClassName="gap-12"
        >
          {visibleFieldGroups.map((group, index) => {
            // Skip rendering empty groups (all fields hidden by conditional logic)
            if (group.fields.length === 0) return null;

            return (
              <Fieldset
                key={group.header || `group-${index}`}
                header={group.header}
                description={group.description}
                hideHeader={hideFieldsetHeaders || !group.header}
              >
                {group.fields.map(renderField)}
              </Fieldset>
            );
          })}
        </Form>
      </div>
      <StickyActionBar
        primaryAction={{
          label: primaryButtonText,
          onClick: onSubmit,
          disabled: isSubmitting,
          visuallyDisabled: !isSubmitting && !isValid,
          isLoading: isSubmitting,
        }}
        secondaryAction={
          shouldShowCancel
            ? {
                label: "Cancel",
                onClick: onCancel,
              }
            : undefined
        }
      />
    </>
  );
}

ProgramQuizForm.displayName = "ProgramQuizForm";

export default ProgramQuizForm;
